/**
 * System status → Jobs: every kind of job, filtered by kind, state and time. 1.2.1: grouped under their main job (a
 * watch item with its ingest, QC and agent jobs below it; a migration with its transfers), expanded and collapsed per
 * row, paged with Load more, and a ⋯ menu per row with the cancel, run again and retry its state and your permissions
 * allow.
 */
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api, errorMessage } from '../../../api/client';
import { usePaged } from '../../../api/hooks/paged';
import { Segmented } from '../../../components/Segmented';
import { StatusDot, jobStatusTone } from '../../../components/Status';
import { TimeRange, useTimeRange } from '../../../components/TimeRange';
import { TableScroll } from '../../../components/TableScroll';
import { LoadMore } from '../../../components/LoadMore';
import { RowMenu } from '../../../components/RowMenu';
import { toast } from '../../../components/Toasts';
import { When, elapsed } from '../../../lib/format';
import { encoderText } from '../../../lib/encoder';
import { ProgressLine, QueuedPill } from '../../../components/JobProgress';

interface JobAction { key: string; label: string; method: 'POST'; path: string; body?: Record<string, unknown>; confirm?: string }
interface JobRow { kind: string; id: string; status: string; subject: string; detail: string | null; step: string | null; error: string | null; link: string; createdAt: string; startedAt: string | null; finishedAt: string | null;
  /** Progress and Queued (0.95.0). */
  percent: number | null; waiting: boolean; waitingSince: string | null;
  /** 1.2.1: grouping, actions and the proxy's encoder. */
  parentKind: string | null; parentId: string | null; childCount: number; childFailed: boolean; inMigration: boolean; actions: JobAction[]; encoder: string | null; decoder: string | null }
interface JobPage { items: JobRow[]; nextCursor?: string | null }

const KINDS = ['all', 'ingest', 'transfer', 'export', 'subtitles', 'qc', 'workflow', 'agent', 'watch', 'live', 'migration'];
const kindLabel = (k: string) => (k === 'all' ? 'All kinds' : k === 'agent' ? 'Agent jobs' : k === 'watch' ? 'Watch folders' : k === 'live' ? 'Live recordings' : k === 'subtitles' ? 'Subtitles' : k === 'qc' ? 'QC runs' : `${k[0]!.toUpperCase()}${k.slice(1)}s`);
const rowKey = (j: { kind: string; id: string }) => `${j.kind}:${j.id}`;
const REFRESH_MS = 5000;

export function Jobs() {
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [migrations, setMigrations] = useState<'group' | 'hide' | 'show'>('group');
  // Expanded rows stay open across the 5-second refresh and Load more.
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const time = useTimeRange('all');
  const w = time.window();
  const base = new URLSearchParams({ kind, status, migrations, ...(w.from ? { from: w.from } : {}), ...(w.to ? { to: w.to } : {}) });
  const jobs = usePaged<JobRow, JobPage>({
    queryKey: ['system-jobs', 'tree', kind, status, migrations, time.key], url: `/api/v1/system/jobs?view=tree&${base}`, pageSize: 100, refetchInterval: REFRESH_MS,
  });
  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const items = jobs.data?.items ?? [];
  return (
    <section className="panel-card" data-testid="jobs-view">
      <div className="jobs-toolbar">
        <Segmented value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k, label: kindLabel(k) }))} />
        <Segmented value={status} onChange={setStatus} options={['all', 'active', 'failed', 'done'].map((k) => ({ value: k, label: k[0]!.toUpperCase() + k.slice(1) }))} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} data-testid="jobs-time"><TimeRange value={time.value} onChange={time.setValue} /></div>
        <label className="jobs-migrations">
          <span className="faint">Migration transfers</span>
          <select className="select" value={migrations} onChange={(e) => setMigrations(e.target.value as typeof migrations)} data-testid="jobs-migrations" aria-label="Migration transfers">
            <option value="group">Grouped</option>
            <option value="hide">Hidden</option>
            <option value="show">Show each</option>
          </select>
        </label>
      </div>
      <TableScroll>
        <table className="table compact jobs-table">
          <thead><tr><th>Kind</th><th>Subject</th><th>Detail</th><th>Status</th><th>Started · took</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {items.map((j) => <JobRows key={rowKey(j)} job={j} depth={0} open={open} toggle={toggle} query={base} />)}
            {jobs.data && items.length === 0 && <tr><td colSpan={6} className="muted">No jobs match.</td></tr>}
          </tbody>
        </table>
      </TableScroll>
      <LoadMore list={jobs} shown={items.length} noun="main jobs" />
    </section>
  );
}

/** A row and, when it is expanded, its children (fetched with parent=) below it, indented. */
function JobRows({ job, depth, open, toggle, query }: { job: JobRow; depth: number; open: Set<string>; toggle: (k: string) => void; query: URLSearchParams }) {
  const k = rowKey(job);
  const expanded = open.has(k) && job.childCount > 0;
  return (
    <>
      <JobLine job={job} depth={depth} expanded={expanded} onToggle={() => toggle(k)} />
      {expanded && <JobChildren parent={job} depth={depth + 1} open={open} toggle={toggle} query={query} />}
    </>
  );
}

function JobChildren({ parent, depth, open, toggle, query }: { parent: JobRow; depth: number; open: Set<string>; toggle: (k: string) => void; query: URLSearchParams }) {
  // The time window and the migrations choice apply to children too; kind and status do not.
  const q = new URLSearchParams({ migrations: query.get('migrations') ?? 'group', parent: rowKey(parent), limit: '500', ...(query.get('from') ? { from: query.get('from')! } : {}), ...(query.get('to') ? { to: query.get('to')! } : {}) });
  const children = useQuery({
    queryKey: ['system-jobs', 'children', rowKey(parent), q.toString()],
    queryFn: () => api<JobPage>(`/api/v1/system/jobs?${q}`),
    refetchInterval: REFRESH_MS,
  });
  if (!children.data) return <tr className="job-child"><td colSpan={6} className="faint" style={{ paddingLeft: 12 + depth * 22 }}>Loading…</td></tr>;
  return (
    <>
      {children.data.items.map((c) => <Fragment key={rowKey(c)}><JobRows job={c} depth={depth} open={open} toggle={toggle} query={query} /></Fragment>)}
      {children.data.nextCursor && <tr className="job-child"><td colSpan={6} className="faint" style={{ paddingLeft: 12 + depth * 22 }}>The first 500 are shown; open {parent.kind === 'migration' ? 'the migration' : 'the job'} for the rest.</td></tr>}
    </>
  );
}

function JobLine({ job: j, depth, expanded, onToggle }: { job: JobRow; depth: number; expanded: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const run = async (a: JobAction) => {
    if (a.confirm && !window.confirm(a.confirm)) return;
    try {
      await api(a.path, { method: a.method, json: a.body ?? {} });
      toast(`${a.label}: done`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
    await qc.invalidateQueries({ queryKey: ['system-jobs'] });
  };
  const codec = j.kind === 'ingest' ? encoderText(j.encoder, j.decoder) : null;
  return (
    <tr data-testid="job-row" data-kind={j.kind} data-depth={depth} className={depth ? 'job-child' : undefined}>
      <td className="muted job-kind" style={{ paddingLeft: 12 + depth * 22 }}>
        {j.childCount > 0
          ? <button type="button" className="job-toggle" aria-expanded={expanded} aria-label={`${expanded ? 'Collapse' : 'Expand'} ${j.kind} ${j.subject}`} onClick={onToggle} data-testid="job-expand">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{j.kind}
            </button>
          : <span className="job-toggle-space">{j.kind}</span>}
        {j.childCount > 0 && (
          <span className="faint job-count" data-testid="job-children">
            {j.childCount} job{j.childCount === 1 ? '' : 's'}{j.childFailed && <span className="job-failed-dot" title="A job below this one failed" aria-label="a job below failed" />}
          </span>
        )}
      </td>
      <td className="wrap" style={{ minWidth: 150 }}><Link to={j.link}>{j.subject}</Link></td>
      <td className="muted wrap break-any" style={{ minWidth: 180 }} title={j.error ?? j.detail ?? ''}>
        {j.detail}{j.step ? ` · ${j.step}` : ''}
        {codec && <div className="faint" style={{ fontSize: 12 }} data-testid="job-encoder">{codec}</div>}
        {j.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{j.error}</div>}
      </td>
      <td className="nowrap">
        {j.waiting ? <QueuedPill since={j.waitingSince} /> : <StatusDot tone={jobStatusTone(j.status)}>{j.status.replace('_', ' ')}</StatusDot>}
        {j.status === 'running' && !j.waiting && (j.percent != null || ['ingest', 'export', 'transfer'].includes(j.kind)) && <ProgressLine percent={j.percent} />}
      </td>
      <td className="muted nowrap">
        <When iso={j.createdAt} />
        {j.finishedAt && <div className="mono faint" style={{ fontSize: 12 }} title="How long it took">took {elapsed(j.startedAt ?? j.createdAt, j.finishedAt)}</div>}
      </td>
      <td className="actions">
        <RowMenu label={`Actions for ${j.kind} ${j.subject}`} testId="job-menu"
          items={j.actions.map((a) => ({ key: a.key, label: a.label, danger: a.key === 'cancel', onSelect: () => void run(a) }))} />
      </td>
    </tr>
  );
}
